import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Settings, PlayCircle, BarChart3 } from "lucide-react";

const sections = [
  {
    title: "Set Up Your Compset",
    description:
      "Define your competitive set by selecting hotels you want to track and compare against.",
    icon: Settings,
    video: "/videos/how-it-works/step1.mp4",
    points: [
      "Add your hotel details",
      "Select relevant competitors",
      "Configure tracking preferences",
    ],
  },
  {
    title: "Run & Collect Data",
    description:
      "Execute your compset to start collecting real-time pricing and market data.",
    icon: PlayCircle,
    video: "/videos/how-it-works/step2.mp4",
    points: [
      "Trigger data collection",
      "Fetch OTA pricing across platforms",
      "Monitor real-time updates",
    ],
  },
  {
    title: "Analyze Your Dashboard",
    description:
      "Use insights from your dashboard to understand pricing trends and optimize revenue.",
    icon: BarChart3,
    video: "/videos/how-it-works/step3.mp4",
    points: [
      "Track rate parity and undercuts",
      "Compare against competitors",
      "Leverage AI insights for pricing decisions",
    ],
  },
  {
    title: "Leverage AI Insights",
    description:
      "Use AI-powered analysis to identify pricing opportunities and optimize your revenue strategy.",
    icon: Sparkles,
    video: "/videos/how-it-works/step4.mp4",
    points: [
      "Detect pricing trends automatically",
      "Get AI-driven rate recommendations",
      "Identify market opportunities faster",
    ],
  }
];

function VideoBlock({ src }) {
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden border bg-black">
      <video
        src={src}
        className="w-full h-full object-cover"
        controls
        playsInline
      />
    </div>
  );
}

export default function HowItWorksDashboard() {
  return (
    <div className="space-y-8 max-w-5xl mx-auto">

      {/* Header */}
      <div>
        <Badge variant="outline" className="mb-3">
          Product Guide
        </Badge>
        <h1 className="text-2xl font-bold">How to Use Prowen</h1>
        <p className="text-muted-foreground mt-1">
          Learn how to set up your compset, collect data, and analyze insights to maximize revenue.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.map((section, index) => (
          <Card key={index} className="border border-border/60">
            <CardContent className="p-6 grid md:grid-cols-2 gap-6 items-center">

              {/* Content */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <section.icon className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">{section.title}</h2>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  {section.description}
                </p>

                <ul className="space-y-2 text-sm">
                  {section.points.map((point, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-primary" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Video */}
              <VideoBlock src={section.video} />

            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}